import React, { useState, useRef, useCallback } from 'react';
import { Button, Upload, message, Progress, Tooltip, Modal } from 'antd';
import {
  UploadOutlined,
  CameraOutlined,
  DeleteOutlined,
  PlusOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import Webcam from 'react-webcam';
import { uploadMedia } from '../../utils/cloudinary';
import './index.css';

const { Dragger } = Upload;

const WebcamCapture = ({ handler, closeWebcam, photos, onCapture, clientId }) => {
  const webcamRef = useRef(null);
  const [isPreview, setIsPreview] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);

  const capture = useCallback(async () => {
    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setCapturedImage(imageSrc);
        setIsPreview(true);
      } else {
        message.error('Failed to capture photo. Please try again.');
      }
    } catch (error) {
      console.error('Capture error:', error);
      message.error('Failed to capture photo. Please try again.');
    }
  }, [webcamRef]);

  const acceptPhoto = async () => {
    try {
      const url = await uploadMedia(capturedImage, 'measurement_photo', { clientId });
      const updatedPhotos = [...photos, url];
      handler(updatedPhotos);
      message.success('Photo captured and uploaded successfully!');
      closeWebcam();
    } catch (error) {
      message.error('Failed to upload photo. Please try again.');
    }
  };

  const declinePhoto = () => {
    setCapturedImage(null);
    setIsPreview(false);
  };

  return (
    <div className='webcam-container'>
      {!isPreview && (
        <div className='webcam-capture'>
          <div className='webcam-header'>
            <h4>📸 Take Progress Photo</h4>
            <p>Position yourself and click capture when ready</p>
            {window.innerWidth <= 768 && (
              <p
                style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}
              >
                💡 Tip: Hold your phone vertically for best results
              </p>
            )}
          </div>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat='image/jpeg'
            className='webcam-video'
            videoConstraints={{
              width: { ideal: window.innerWidth <= 768 ? 320 : 400 },
              height: { ideal: window.innerWidth <= 768 ? 240 : 300 },
              facingMode: 'user',
            }}
          />
          <div className='webcam-actions'>
            <Button
              type='primary'
              icon={<CameraOutlined />}
              onClick={capture}
              size='large'
            >
              Capture Photo
            </Button>
            <Button onClick={closeWebcam}>Cancel</Button>
          </div>
        </div>
      )}
      {isPreview && capturedImage && (
        <div className='webcam-preview'>
          <div className='preview-header'>
            <h4>Review Your Photo</h4>
            <p>Make sure it's clear and well-lit</p>
          </div>
          <img
            className='captured-preview'
            src={capturedImage}
            alt='Captured'
          />
          <div className='preview-actions'>
            <Button type='primary' onClick={acceptPhoto} size='large'>
              Accept Photo
            </Button>
            <Button onClick={declinePhoto}>Retake</Button>
          </div>
        </div>
      )}
    </div>
  );
};

// `clientId`: whose photos these are (the client a coach is measuring, or
// the signed-in client) — the upload is filed under that client.
const ProgressUpload = ({ photos, handler, location, clientId }) => {
  const [isWebcamActive, setIsWebcamActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const isUploadingRef = useRef(false);

  const closeWebcamWindow = () => {
    setIsWebcamActive(false);
  };

  const removePicture = (index) => {
    console.log('removePicture called with index:', index);
    console.log('isWebcamActive:', isWebcamActive);
    console.log('Current photos:', photos);

    // Create a new array with the same length, replacing the deleted photo with null
    const updatedPhotos = [...photos];
    updatedPhotos[index] = null;

    console.log('Updated photos:', updatedPhotos);

    handler(updatedPhotos);
    message.success('Photo removed successfully');
  };

  const handleFileUpload = async (files) => {
    // Prevent multiple simultaneous uploads
    if (isUploadingRef.current) {
      console.log('Upload already in progress, skipping...');
      return;
    }

    // Count only actual photos (non-null values)
    const actualPhotoCount = photos.filter((photo) => photo !== null).length;

    if (actualPhotoCount + files.length > 3) {
      message.error('Maximum 3 photos allowed');
      return;
    }

    isUploadingRef.current = true;
    setLoading(true);
    setUploadProgress(0);
    const readedPhotos = [];
    let uploadedCount = 0;
    let totalSteps = files.length * 3; // Each file has 3 steps: read + validate + upload
    let completedSteps = 0;

    const addPhotos = () => {
      // Find the first available slot (null position) for each photo
      const updatedPhotos = [...photos];
      let photoIndex = 0;

      for (
        let i = 0;
        i < updatedPhotos.length && photoIndex < readedPhotos.length;
        i++
      ) {
        if (updatedPhotos[i] === null && readedPhotos[photoIndex]) {
          updatedPhotos[i] = readedPhotos[photoIndex];
          photoIndex++;
        }
      }

      // If there are still photos to add and we haven't reached the limit
      while (photoIndex < readedPhotos.length && updatedPhotos.length < 3) {
        updatedPhotos.push(readedPhotos[photoIndex]);
        photoIndex++;
      }

      handler(updatedPhotos);
      setLoading(false);
      setUploadProgress(0);
      isUploadingRef.current = false;
      message.success(`${files.length} photo(s) uploaded successfully!`);
    };

    for (const [index, photo] of files.entries()) {
      const reader = new FileReader();
      reader.readAsDataURL(photo);

      reader.onload = () => {
        completedSteps++;
        // Add small delay for smoother progress animation
        setTimeout(() => {
          setUploadProgress((completedSteps / totalSteps) * 100);
        }, 100);

        let image = new Image();
        image.src = reader.result;

        image.onload = () => {
          // Validation step completed
          completedSteps++;
          setTimeout(() => {
            setUploadProgress((completedSteps / totalSteps) * 100);
          }, 100);

          if (image.width > image.height) {
            message.error(
              'Please upload vertical images (portrait orientation)'
            );
            uploadedCount++;
            completedSteps++;
            setTimeout(() => {
              setUploadProgress((completedSteps / totalSteps) * 100);
            }, 100);

            if (uploadedCount === files.length) {
              setLoading(false);
              setUploadProgress(0);
              isUploadingRef.current = false;
            }
            return;
          }

          uploadMedia(reader.result, 'measurement_photo', { clientId })
            .then((url) => {
              readedPhotos[index] = url;
              uploadedCount++;
              completedSteps++;
              setTimeout(() => {
                setUploadProgress((completedSteps / totalSteps) * 100);
              }, 100);

              if (uploadedCount === files.length) {
                addPhotos();
              }
            })
            .catch(() => {
              uploadedCount++;
              completedSteps++;
              setTimeout(() => {
                setUploadProgress((completedSteps / totalSteps) * 100);
              }, 100);

              if (uploadedCount === files.length) {
                setLoading(false);
                setUploadProgress(0);
                isUploadingRef.current = false;
              }
            });
        };
      };
    }
  };

  const uploadProps = {
    name: 'file',
    multiple: true,
    accept: '.jpg,.jpeg,.png',
    showUploadList: false,
    beforeUpload: (file) => {
      const isValidType = ['image/jpeg', 'image/jpg', 'image/png'].includes(
        file.type
      );
      if (!isValidType) {
        message.error('You can only upload JPG/PNG files!');
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('Image must be smaller than 5MB!');
        return false;
      }
      return false; // Prevent auto upload
    },
    onChange: (info) => {
      if (info.fileList.length > 0) {
        const files = info.fileList.map((f) => f.originFileObj);
        handleFileUpload(files);
      }
    },
  };

  const getPhotoPosition = (index) => {
    const positions = ['Front', 'Side', 'Back'];
    return positions[index] || 'Photo';
  };

  return (
    <div className='enhanced-uploader'>
      {/* Header Section */}
      <div className='uploader-header'>
        <div className='uploader-title'>
          <h4>📸 Progress Photos</h4>
          <p>Upload up to 3 photos to track your progress</p>
        </div>
        <Tooltip title='Photos help track your fitness journey. Use portrait orientation for best results.'>
          <InfoCircleOutlined className='info-icon' />
        </Tooltip>
      </div>

      {/* Upload Area */}
      <div className='upload-area'>
        <Dragger {...uploadProps} className='upload-dragger'>
          <div className='upload-content'>
            <PlusOutlined className='upload-icon' />
            <p className='upload-text'>Click or drag photos here</p>
            <p className='upload-hint'>Supports JPG, PNG up to 5MB each</p>
            <p className='upload-limit'>Maximum 3 photos</p>
          </div>
        </Dragger>

        <div className='upload-actions'>
          <Button
            type='primary'
            icon={<CameraOutlined />}
            onClick={() => setIsWebcamActive(true)}
            className='webcam-btn'
          >
            Take Photo
          </Button>
          <Button
            type='secondary'
            icon={<UploadOutlined />}
            onClick={() => fileInputRef.current?.click()}
          >
            Choose Files
          </Button>
        </div>

        <input
          ref={fileInputRef}
          type='file'
          name='photo'
          accept='.jpg,.jpeg,.png'
          className='hidden-file-input'
          id={location}
          multiple
          onChange={(event) => {
            if (event.target.files.length > 0) {
              handleFileUpload(Array.from(event.target.files));
            }
            event.target.value = '';
          }}
        />
      </div>

      {/* Progress Bar */}
      {loading && (
        <div className='upload-progress'>
          <Progress
            percent={Math.round(uploadProgress)}
            status='active'
            strokeColor={{
              '0%': '#2761f1',
              '100%': '#1e40af',
            }}
          />
          <p className='progress-text'>
            Uploading photos... {Math.round(uploadProgress)}%
          </p>
        </div>
      )}

      {/* Photo Previews */}
      {photos.length > 0 && (
        <div className='photo-previews'>
          <div className='previews-header'>
            <span className='photo-count'>
              {photos.filter((photo) => photo !== null).length} photo(s)
              uploaded
            </span>
          </div>
          <div className='previews-grid'>
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                className={`preview-item ${
                  photos[index] ? 'has-photo' : 'empty'
                }`}
              >
                {photos[index] ? (
                  <>
                    <div className='preview-image-container'>
                      <img
                        src={photos[index]}
                        alt={`Progress photo ${index + 1}`}
                        className='preview-image'
                      />
                      <span
                        className='remove-photo-icon'
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('Remove photo clicked for index:', index);
                          removePicture(index);
                        }}
                      >
                        ×
                      </span>
                    </div>
                    <div className='photo-position'>
                      {getPhotoPosition(index)}
                    </div>
                  </>
                ) : (
                  <div
                    className='empty-slot'
                    onClick={() => fileInputRef.current?.click()}
                    style={{ cursor: 'pointer' }}
                  >
                    <PlusOutlined />
                    <span>Add Photo</span>
                    <span
                      style={{
                        fontSize: '10px',
                        color: '#9ca3af',
                        marginTop: '4px',
                      }}
                    >
                      Click to add
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Webcam Modal */}
      <Modal
        title={
          window.innerWidth <= 768 ? '📸 Take Photo' : 'Take Progress Photo'
        }
        open={isWebcamActive}
        onCancel={closeWebcamWindow}
        footer={null}
        width={window.innerWidth <= 768 ? '95%' : 600}
        centered
        destroyOnClose
        className='webcam-modal'
        closeIcon={<span style={{ fontSize: '18px', padding: '4px' }}>✕</span>}
      >
        <WebcamCapture
          handler={handler}
          closeWebcam={closeWebcamWindow}
          photos={photos}
          clientId={clientId}
        />
      </Modal>
    </div>
  );
};

export default ProgressUpload;
